import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GraduationCap, Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const googleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Register() {
    const [form, setForm] = useState({ name: "", email: "", password: "", role: "student" });
    const [loading, setLoading] = useState(false);
    const { register } = useAuth();
    const navigate = useNavigate();
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await register(form);
            toast.success("Account created — welcome to LARUTA!");
            navigate("/dashboard");
        } catch (err) {
            toast.error(apiError(err));
        } finally { setLoading(false); }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-background">
            <div className="hidden lg:flex flex-col justify-between bg-secondary text-secondary-foreground p-12 grid-bg">
                <Link to="/" className="flex items-center gap-2"><span className="grid h-9 w-9 place-items-center rounded-lg bg-primary text-primary-foreground"><GraduationCap className="h-5 w-5" /></span><span className="font-heading text-lg font-extrabold">LARUTA<span className="text-primary">.</span></span></Link>
                <div><h2 className="font-heading text-3xl font-bold leading-tight">Build real skills. Earn a verified Digital Passport.</h2><p className="mt-4 text-secondary-foreground/70">Join learners of every generation on LARUTA.</p></div>
                <p className="font-mono text-sm text-secondary-foreground/40">Experience &gt; Certificate</p>
            </div>
            <div className="flex items-center justify-center p-6 sm:p-12">
                <div className="w-full max-w-sm">
                    <h1 className="font-heading text-2xl font-bold">Create your account</h1>
                    <p className="mt-1 text-sm text-foreground/60">Start learning in minutes.</p>
                    <form onSubmit={submit} className="mt-8 space-y-4">
                        <div><Label htmlFor="name">Full name</Label><Input id="name" data-testid="register-name-input" required value={form.name} onChange={set("name")} className="mt-1.5" /></div>
                        <div><Label htmlFor="email">Email</Label><Input id="email" type="email" data-testid="register-email-input" required value={form.email} onChange={set("email")} className="mt-1.5" /></div>
                        <div><Label htmlFor="password">Password</Label><Input id="password" type="password" data-testid="register-password-input" required minLength={6} value={form.password} onChange={set("password")} className="mt-1.5" /></div>
                        <div>
                            <Label>I am a…</Label>
                            <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                                <SelectTrigger className="mt-1.5" data-testid="register-role-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="student">Student / Learner</SelectItem>
                                    <SelectItem value="parent">Parent</SelectItem>
                                    <SelectItem value="teacher">Teacher / Instructor</SelectItem>
                                    <SelectItem value="mentor">Mentor</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <Button type="submit" disabled={loading} className="w-full rounded-full" data-testid="register-submit-btn">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}</Button>
                    </form>
                    <div className="my-5 flex items-center gap-3 text-xs text-foreground/40"><span className="flex-1 h-px bg-border" /> OR <span className="flex-1 h-px bg-border" /></div>
                    <Button variant="outline" onClick={googleLogin} className="w-full rounded-full" data-testid="google-register-btn"><Mail className="mr-2 h-4 w-4" /> Continue with Google</Button>
                    <p className="mt-6 text-center text-sm text-foreground/60">Already have an account? <Link to="/login" className="text-primary font-medium">Log in</Link></p>
                </div>
            </div>
        </div>
    );
}
